import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { BackButton } from 'foundations-components/transitional/components/BackButton';
import { Big } from 'foundations-components/transitional/components/Big';
import { EditorToolbar } from 'foundations-components/transitional/components/EditorToolbar';
import { HR } from 'foundations-components/transitional/components/HR';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Link } from 'foundations-components/transitional/components/Link';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Small } from 'foundations-components/transitional/components/Small';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ACTIONS, SCREENS } from '../../../../constants/Tracking';
import { AppContext, appActions } from '../../../../contexts/app/context';
import { PageLayoutPropsContext } from '../../../../contexts/pageLayout/PropsContext';
import HubSpotIcon from '../HubSpotIcon';
import NavBarTitle from '../NavBarTitle';
import { StyledDownCaratIconWrapper } from './NavBar.style';
import { DownCaratIcon } from 'foundations-assets/icons/components/DownCaratIcon';
import { PreviousIcon } from 'foundations-assets/icons/components/PreviousIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function NavBar({
  showBackButton,
  back
}) {
  const {
    resetStateBetweenCallObjects,
    currentAccount,
    usageTracker,
    unauthorizedRoutesParam,
    routes,
    initMethod,
    portalId,
    canViewParticipants,
    isDemoApp
  } = useContext(PageLayoutPropsContext);
  const {
    isInMeeting,
    isPastMeeting,
    appName
  } = useContext(AppContext);
  const history = useHistory();
  const isInLivePastMeeting = useMemo(() => !!isInMeeting && isPastMeeting, [isInMeeting, isPastMeeting]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const backToLiveMeeting = useCallback(() => {
    const screenName = history.location.pathname.split('/').pop();
    resetStateBetweenCallObjects();
    initMethod();
    appActions.setIsPastMeeting(false);
    history.push(`/${portalId}${canViewParticipants ? routes.contacts : routes.associations}`);
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[screenName === 'contacts' ? 'participants' : screenName]
    }, ACTIONS.planner.clickBackToLiveMeetingButton));
  }, [canViewParticipants, history, initMethod, portalId, resetStateBetweenCallObjects, routes.associations, routes.contacts, usageTracker]);
  const startSlot = useMemo(() => {
    if (isDemoApp) {
      return /*#__PURE__*/_jsx(View, {
        position: "relative",
        insetBlockStart: "100",
        children: /*#__PURE__*/_jsx(HubSpotIcon, {})
      });
    }
    if (isInLivePastMeeting) {
      return /*#__PURE__*/_jsx(View, {
        cursor: "pointer",
        children: /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          "data-test-id": `${appName}-back-to-live-meeting-button`,
          children: /*#__PURE__*/_jsx(IconButton, {
            use: "unstyled",
            onClick: backToLiveMeeting,
            children: /*#__PURE__*/_jsx(PreviousIcon, {
              color: "icon-secondary-default",
              size: "800"
            })
          })
        })
      });
    }
    return /*#__PURE__*/_jsx(View, {
      position: "relative",
      insetBlockStart: "100",
      children: /*#__PURE__*/_jsx(Link, {
        href: `${routes.base}?${unauthorizedRoutesParam}=false`,
        children: /*#__PURE__*/_jsx(HubSpotIcon, {})
      })
    });
  }, [appName, backToLiveMeeting, isDemoApp, isInLivePastMeeting, routes.base, unauthorizedRoutesParam]);
  return /*#__PURE__*/_jsx(EditorToolbar, {
    titleText: /*#__PURE__*/_jsx(NavBarTitle, {}),
    startSlot: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [startSlot, showBackButton ? /*#__PURE__*/_jsx(View, {
        cursor: "pointer",
        position: "relative",
        insetBlockStart: "50",
        children: /*#__PURE__*/_jsx(BackButton, {
          marginInlineStart: "200",
          children: /*#__PURE__*/_jsx(Link, {
            onClick: back,
            use: "on-dark",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.header.backToPlanner"
            })
          })
        })
      }) : null]
    }),
    endSlot: isDemoApp ? null : /*#__PURE__*/_jsx(Popover, {
      open: isPopoverOpen,
      closeOnOutsideClick: true,
      onOpenChange: e => setIsPopoverOpen(e.currentTarget.value),
      placement: "bottom left",
      autoPlacement: false,
      content: {
        header: /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(Big, {
            weight: "bold",
            "data-test-id": `${appName}-current-account-name`,
            children: currentAccount.accountName
          })
        }),
        body: /*#__PURE__*/_jsxs(_Fragment, {
          children: [currentAccount.hublet && /*#__PURE__*/_jsxs(Small, {
            use: "help",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "hubspotAccount.hublet"
            }), ' ', /*#__PURE__*/_jsx("span", {
              "data-test-id": `${appName}-current-account-hublet`,
              children: currentAccount.hublet
            }), /*#__PURE__*/_jsx("br", {})]
          }), /*#__PURE__*/_jsxs(Small, {
            use: "help",
            "data-test-id": `${appName}-current-account-id`,
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "hubspotAccount.id"
            }), ' ', currentAccount.id]
          })]
        }),
        footer: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(HR, {
            distance: "small"
          }), /*#__PURE__*/_jsx(Link, {
            href: `${routes.base}${routes.accounts}?${unauthorizedRoutesParam}=false`,
            children: I18n.text('app.changePortal')
          })]
        })
      },
      children: /*#__PURE__*/_jsx(Link, {
        use: "on-dark",
        onClick: () => setIsPopoverOpen(prevOpen => !prevOpen),
        children: /*#__PURE__*/_jsxs(Flex, {
          flexWrap: "nowrap",
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.options"
          }), /*#__PURE__*/_jsx(StyledDownCaratIconWrapper, {
            $iconSize: "8px",
            children: /*#__PURE__*/_jsx(DownCaratIcon, {})
          })]
        })
      })
    })
  });
}
export default NavBar;